// League workspace — Divisions view.
//
// Two things live here:
//   1. "Create Division" primary action → portalled modal form with
//      Season (default "Season-<current year>"), Division Name, and
//      Number of Teams.
//   2. Overview grid — one rich card per division, grouped by season.
//      Each card shows fill progress and the teams currently assigned
//      to that division (with a dashed placeholder for open slots).

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Layers, Plus, X, Pencil, Trash2, Trophy, ShieldCheck, Users,
  Calendar, Sparkles, GripVertical
} from "lucide-react";
import { Panel } from "../../components/primitives";
import { useToast } from "../../components/Toast";
import { api } from "../../api";

function currentYear() {
  // Use the ET-derived "today" so late-night users don't see the wrong
  // year briefly. Falls back to plain new Date if the utility is not
  // available in this branch.
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York", year: "numeric"
    }).format(new Date());
  } catch { return String(new Date().getFullYear()); }
}
const DEFAULT_SEASON = () => `Season-${currentYear()}`;

const EMPTY_DIVISION = {
  season: "",
  divisionName: "",
  maxTeams: 6
};

// Rotate accent tones so a season with many divisions still reads as
// distinct blocks. Seeded by division name so the same division keeps
// its color across renders.
const CARD_TONES = ["gold", "cyan", "green", "violet", "orange"];
function toneFor(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return CARD_TONES[h % CARD_TONES.length];
}

function teamMonogram(name = "") {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase() || "?";
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export default function LeagueDivisions({ user, embedded = false }) {
  const toast = useToast();
  const [divisions, setDivisions] = useState([]);
  const [teams,     setTeams]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [busy,      setBusy]      = useState(false);

  const [showForm,  setShowForm]  = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form,      setForm]      = useState(() => ({ ...EMPTY_DIVISION, season: DEFAULT_SEASON() }));

  // Drag-reorder state — refs for synchronous access in event handlers,
  // state for visual styling. Reordering only rearranges divisions
  // within the same season lane.
  const [dragId,     setDragId]     = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const dragIdRef       = useRef(null);
  const dragSeasonRef   = useRef(null);

  function onDragStart(e, division) {
    dragIdRef.current     = division.id;
    dragSeasonRef.current = division.season;
    setDragId(division.id);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("application/x-division", String(division.id)); } catch { /* ignore */ }
  }
  function onDragEnd() {
    dragIdRef.current = null;
    dragSeasonRef.current = null;
    setDragId(null);
    setDragOverId(null);
  }
  function onDragOver(e, division) {
    if (dragIdRef.current == null) return;
    // Only allow drop within the same season lane — cross-season reorder
    // would confuse the "N of M teams" season summary.
    if (dragSeasonRef.current !== division.season) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverId !== division.id) setDragOverId(division.id);
  }
  async function onDrop(e, targetDivision) {
    const draggedId = dragIdRef.current;
    if (draggedId == null) return;
    if (dragSeasonRef.current !== targetDivision.season) return;
    e.preventDefault();
    dragIdRef.current = null;
    dragSeasonRef.current = null;
    setDragId(null); setDragOverId(null);
    if (draggedId === targetDivision.id) return;

    const currentOrder = divisions;
    const fromIdx = currentOrder.findIndex(d => d.id === draggedId);
    if (fromIdx < 0) return;

    const next = [...currentOrder];
    const [moved] = next.splice(fromIdx, 1);
    const insertAt = next.findIndex(d => d.id === targetDivision.id);
    next.splice(insertAt < 0 ? next.length : insertAt, 0, moved);

    setDivisions(next); // optimistic
    try {
      await api.reorderDivisions(next.map(d => d.id));
      refresh();
    } catch (err) {
      toast.error(err.message || "Couldn't save the new order.");
      refresh();
    }
  }

  async function refresh() {
    setLoading(true);
    try {
      const [d, t] = await Promise.all([
        api.listDivisions(), api.listRegisteredTeams()
      ]);
      setDivisions(d);
      setTeams(t);
    } catch (err) {
      toast.error(err.message || "Couldn't load divisions.");
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!showForm) return;
    const onKey = e => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showForm]);

  function openCreate() {
    setEditingId(null);
    setForm({ ...EMPTY_DIVISION, season: DEFAULT_SEASON() });
    setShowForm(true);
  }
  function openEdit(d) {
    setEditingId(d.id);
    setForm({ season: d.season, divisionName: d.name, maxTeams: d.maxTeams });
    setShowForm(true);
  }
  function close() { setShowForm(false); setEditingId(null); }

  async function submit(e) {
    e.preventDefault();
    if (!form.season.trim())       { toast.error("Season is required."); return; }
    if (!form.divisionName.trim()) { toast.error("Division name is required."); return; }
    if (!(form.maxTeams > 0))      { toast.error("Number of teams must be positive."); return; }
    setBusy(true);
    try {
      const payload = {
        season:       form.season.trim(),
        divisionName: form.divisionName.trim(),
        maxTeams:     Number(form.maxTeams)
      };
      if (editingId) {
        await api.updateDivision(editingId, payload);
        toast.success("Division updated.");
      } else {
        await api.createDivision(payload);
        toast.success("Division created.");
      }
      close();
      await refresh();
    } catch (err) { toast.error(err.message || "Save failed."); }
    finally { setBusy(false); }
  }

  async function remove(d) {
    const inUse = teams.filter(t => t.divisionId === d.id).length;
    const msg = inUse > 0
      ? `Delete "${d.name}"? ${inUse} team${inUse === 1 ? "" : "s"} will be moved back to Unassigned.`
      : `Delete "${d.name}"?`;
    if (!confirm(msg)) return;
    try {
      await api.deleteDivision(d.id);
      toast.success("Division removed.");
      await refresh();
    } catch (err) { toast.error(err.message || "Couldn't remove."); }
  }

  // Group divisions by season for the overview lanes.
  const bySeason = useMemo(() => {
    const map = new Map();
    for (const d of divisions) {
      if (!map.has(d.season)) map.set(d.season, []);
      map.get(d.season).push(d);
    }
    return [...map.entries()];
  }, [divisions]);

  const teamsByDivision = useMemo(() => {
    const map = new Map();
    for (const t of teams) {
      if (t.divisionId == null) continue;
      if (!map.has(t.divisionId)) map.set(t.divisionId, []);
      map.get(t.divisionId).push(t);
    }
    // Alphabetise every division's team list — case-insensitive by name.
    for (const [k, list] of map) {
      list.sort((a, b) =>
        (a.teamName || "").localeCompare(b.teamName || "", undefined, { sensitivity: "base" })
      );
      map.set(k, list);
    }
    return map;
  }, [teams]);

  const unassignedCount = useMemo(
    () => teams.filter(t => t.divisionId == null && t.status !== "Rejected").length,
    [teams]
  );

  return (
    <div className={embedded ? "stack" : "stack league-view"}>
      {!embedded && (
        <div className="builderTop">
          <div>
            <p>Pittsburgh Cricket League</p>
            <h2>Divisions</h2>
          </div>
          <div>
            <button className="btn" onClick={openCreate}>
              <Plus size={16}/> Create Division
            </button>
          </div>
        </div>
      )}
      {embedded && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button className="btn" onClick={openCreate}>
            <Plus size={16}/> Create Division
          </button>
        </div>
      )}

      {/* At-a-glance summary strip */}
      <div className="div-stats">
        <div className="div-stats-card div-stats-card--gold">
          <span><Layers size={14}/> Divisions</span>
          <b>{divisions.length}</b>
        </div>
        <div className="div-stats-card div-stats-card--cyan">
          <span><ShieldCheck size={14}/> Teams Assigned</span>
          <b>{teams.filter(t => t.divisionId != null).length}</b>
        </div>
        <div className="div-stats-card div-stats-card--violet">
          <span><Sparkles size={14}/> Awaiting Assignment</span>
          <b>{unassignedCount}</b>
        </div>
      </div>

      {loading && <p style={{color:"var(--muted)"}}>Loading divisions…</p>}
      {!loading && divisions.length === 0 && (
        <Panel title={<><Layers size={18}/> No divisions yet</>}>
          <div className="league-annEmpty">
            <Layers size={28}/>
            <p>
              No divisions created for this season yet. Use <b>Create Division</b> to
              set up your structure — Season, Division Name, and target Team Count.
            </p>
          </div>
        </Panel>
      )}

      {!loading && bySeason.map(([season, list]) => (
        <section key={season} className="div-season">
          <header className="div-season-head">
            <span className="div-season-chip">
              <Calendar size={12}/> {season}
            </span>
            <h3>{list.length} division{list.length === 1 ? "" : "s"}</h3>
            <small>
              {list.reduce((s, d) => s + (teamsByDivision.get(d.id)?.length || 0), 0)} of{" "}
              {list.reduce((s, d) => s + d.maxTeams, 0)} slots filled
            </small>
          </header>

          <div className="div-cardGrid">
            {list.map(d => {
              const teamsHere = teamsByDivision.get(d.id) || [];
              const tone = toneFor(d.name);
              const emptySlots = Math.max(0, d.maxTeams - teamsHere.length);
              const isFull = teamsHere.length >= d.maxTeams;
              const pct = Math.min(100, Math.round((teamsHere.length / d.maxTeams) * 100));
              const isDragging = dragId === d.id;
              const isDragOver = dragOverId === d.id && dragId != null && dragId !== d.id;
              return (
                <article
                  key={d.id}
                  className={
                    `div-card div-card--${tone}` +
                    (isFull ? " div-card--full" : "") +
                    (isDragging ? " div-card--dragging" : "") +
                    (isDragOver ? " div-card--dragOver" : "")
                  }
                  onDragOver={e => onDragOver(e, d)}
                  onDrop={e => onDrop(e, d)}
                >
                  <header className="div-card-head">
                    {/* Drag handle — grab this to reorder divisions
                        within the same season lane. */}
                    <span
                      role="button"
                      tabIndex={0}
                      className="div-card-grip"
                      draggable="true"
                      onMouseDown={e => e.stopPropagation()}
                      onDragStart={e => onDragStart(e, d)}
                      onDragEnd={onDragEnd}
                      title="Drag to reorder"
                      aria-label="Reorder division"
                    >
                      <GripVertical size={13}/>
                    </span>
                    <div style={{flex:1, minWidth:0}}>
                      <span className="div-card-eyebrow">
                        <Trophy size={11}/> {d.season}
                      </span>
                      <h4>{d.name}</h4>
                    </div>
                    <div className="div-card-actions">
                      <button type="button"
                        className="div-card-iconBtn"
                        onClick={() => openEdit(d)}
                        title="Edit division"
                      ><Pencil size={13}/></button>
                      <button type="button"
                        className="div-card-iconBtn div-card-iconBtn--danger"
                        onClick={() => remove(d)}
                        title="Delete division"
                      ><Trash2 size={13}/></button>
                    </div>
                  </header>

                  <div className="div-card-progress">
                    <div className="div-card-progress-bar">
                      <div className="div-card-progress-fill" style={{ width: `${pct}%` }}/>
                    </div>
                    <div className="div-card-progress-label">
                      <b>{teamsHere.length}</b>
                      <span>/ {d.maxTeams} teams</span>
                      {isFull && <span className="div-card-progress-badge">Full</span>}
                    </div>
                  </div>

                  <div className="div-card-teams">
                    <header>
                      <Users size={12}/>
                      <span>Teams</span>
                    </header>
                    {teamsHere.length === 0 ? (
                      <p className="div-card-teams-empty">
                        No teams assigned yet. Head to Teams Management to drag teams in.
                      </p>
                    ) : (
                      <ul>
                        {teamsHere.map(t => (
                          <li key={t.id}>
                            <span className="div-teamChip">
                              <span className="div-teamChip-monogram">{teamMonogram(t.teamName)}</span>
                              <span>{t.teamName}</span>
                            </span>
                          </li>
                        ))}
                        {emptySlots > 0 && Array.from({ length: Math.min(emptySlots, 4) }).map((_, i) => (
                          <li key={`empty-${i}`}>
                            <span className="div-teamChip div-teamChip--empty">
                              <span className="div-teamChip-monogram div-teamChip-monogram--empty">?</span>
                              <span>Open slot</span>
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      ))}

      {/* Create / edit division modal */}
      {showForm && createPortal((
        <div className="modal" onClick={close}>
          <div className="modalInner" onClick={e => e.stopPropagation()}>
            <button type="button" className="close" onClick={close} aria-label="Close">
              <X size={20}/>
            </button>
            <Panel title={editingId ? "Edit Division" : "Create Division"}>
              <form className="form" onSubmit={submit}>
                <label>Season *</label>
                <input value={form.season}
                  onChange={e => setForm({ ...form, season: e.target.value })}
                  placeholder="Season-2026"/>
                <small style={{color:"var(--muted)", fontSize:11, marginTop:-4}}>
                  Free-text so you can label playoffs, tournaments, or off-season sides however you like.
                </small>

                <label>Division Name *</label>
                <input value={form.divisionName}
                  onChange={e => setForm({ ...form, divisionName: e.target.value })}
                  placeholder="Division A"
                  autoFocus/>

                <label>Number of Teams *</label>
                <input type="number" min="1" max="64" value={form.maxTeams}
                  onChange={e => setForm({ ...form, maxTeams: e.target.value })}
                  placeholder="6"/>
                <small style={{color:"var(--muted)", fontSize:11, marginTop:-4}}>
                  How many teams this division is designed to hold. The overview uses this to
                  render open slots and warn on overfill.
                </small>

                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 6 }}>
                  <button type="button" className="dark" onClick={close} disabled={busy}>Cancel</button>
                  <button type="submit" className="btn" disabled={busy}>
                    {busy ? "Saving…" : editingId ? "Save Changes" : "Create Division"}
                  </button>
                </div>
              </form>
            </Panel>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}
